import { useEffect, useState, useCallback, useRef } from "react";
import { AppstoreOutlined, FolderOpenOutlined, LineChartOutlined, DeleteOutlined } from "@ant-design/icons";
import { Template_Options, Template_keys_description, Template_Main_Keys, productAnalysesKPIFields } from "../constants";
import "./TemplateMaker.css";

import { Popconfirm } from 'antd'

const Local_Template_Options = JSON.parse(JSON.stringify(Template_Options)); // Deep copy to avoid mutating original

function MetricTreeItem({ mainKey, subKey, metric, setSelectedMetric }) {
  const metricInfo = Local_Template_Options[mainKey][subKey].items[metric];
  const handleAddMetric = () => {
    if (mainKey === "Big Number Card (KPIs)" || mainKey === "Pie Chart" || mainKey === "Miscellaneous") {
      setSelectedMetric({
        mainKey,
        subKey,
        metric,
        metricInfo
      });
    } else if (mainKey === "Table (Firm Wide) KPIs" || mainKey === "Table (Projections)" || mainKey === "Product Analysis (KPIs)" || mainKey === "Table (Pricing)" || mainKey === "Chart (Projections)") {
      const newItems = (prev) => {
        const items = prev.mainKey !== mainKey ? [] : prev.items || [];
        items.push({
          subKey,
          metric,
          metricInfo
        });
        return items;
      };

      // For table metrics, we accumulate selected metrics into an items array but start with an empty array if previously none or previously from a different mainKey
      setSelectedMetric(prev => ({
        mainKey,
        items: newItems(prev)
      }));
    }
  };

  return (
    <div
      className="metric-item"
      onClick={handleAddMetric}
    >
      <div className="metric-item-title">
        <LineChartOutlined className="icon-left" />
        <strong>{metric}</strong>
      </div>
      <div className="metric-description">{metricInfo.description}</div>
    </div>
  );
}

function SubCategoryMenu({ mainKey, subKey, setSelectedMetric }) {
  const subCategoryData = Local_Template_Options[mainKey][subKey];
  const metrics = subCategoryData.keys;
  const submenuRef = useRef(null);
  const [openLeft, setOpenLeft] = useState(false);

  useEffect(() => {
    if (submenuRef.current) {
      const rect = submenuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      // Check if submenu would overflow on the right
      const wouldOverflow = rect.right > viewportWidth;
      setOpenLeft(wouldOverflow);
    }
  }, []);

  return (
    <div ref={submenuRef} className={`submenu ${openLeft ? 'submenu-left' : ''}`}>
      <div className="submenu-header">{subKey}</div>
      <div className="submenu-description">{subCategoryData.description}</div>
      <div className="metrics-list">
        {metrics.map((metric) => (
          <MetricTreeItem
            key={metric}
            mainKey={mainKey}
            subKey={subKey}
            metric={metric}
            setSelectedMetric={setSelectedMetric}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryMenu({ mainKey, setSelectedMetric, hoveredSubKey, setHoveredSubKey }) {
  const categoryData = Local_Template_Options[mainKey];
  const subCategories = categoryData.keys;
  const categoryMenuRef = useRef(null);
  const [openLeft, setOpenLeft] = useState(false);

  useEffect(() => {
    if (categoryMenuRef.current) {
      const rect = categoryMenuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      // Check if category menu would overflow on the right
      const wouldOverflow = rect.right > viewportWidth;
      setOpenLeft(wouldOverflow);
    }
  }, []);

  return (
    <div ref={categoryMenuRef} className={`category-menu ${openLeft ? 'category-menu-left' : ''}`}>
      <div className="category-header">{mainKey}</div>
      <div className="category-description">{Template_keys_description[mainKey].description}</div>
      <div className="subcategories-list">
        {subCategories.map((subKey) => (
          <div
            key={subKey}
            className="subcategory-item"
            onMouseEnter={() => setHoveredSubKey(subKey)}
            onMouseLeave={() => setHoveredSubKey(null)}
          >
            <span className="subcategory-name">
              <FolderOpenOutlined className="icon-left" /> {subKey}
            </span>
            {hoveredSubKey === subKey && (
              <SubCategoryMenu
                mainKey={mainKey}
                subKey={subKey}
                setSelectedMetric={setSelectedMetric}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricSelector({ setSelectedMetric }) {
  const [hoveredMainKey, setHoveredMainKey] = useState(null);
  const [hoveredSubKey, setHoveredSubKey] = useState(null);

  return (
    <div className="metric-selector">
      <h2>Select a Metric</h2>
      <div className="main-keys-list">
        {Template_Main_Keys.map((mainKey) => (
          <div
            key={mainKey}
            className="main-key-item"
            onMouseEnter={() => setHoveredMainKey(mainKey)}
            onMouseLeave={() => {
              setHoveredMainKey(null);
              setHoveredSubKey(null);
            }}
          >
            <span className="main-key-name">
              <AppstoreOutlined className="icon-left" /> {mainKey}
            </span>
            {hoveredMainKey === mainKey && (
              <CategoryMenu
                mainKey={mainKey}
                setSelectedMetric={setSelectedMetric}
                hoveredSubKey={hoveredSubKey}
                setHoveredSubKey={setHoveredSubKey}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateMaker({ productName, dataToExport, entitySelected, currentTemplate, setCurrentTemplate, templateSelected }) {
  const [selectedMetric, setSelectedMetric] = useState({
    mainKey: null,
    subKey: null,
    metric: null,
    metricInfo: null
  });
  const [isShaking, setIsShaking] = useState(false);

  const triggerMetricShake = (newMetric) => {
    setSelectedMetric(newMetric);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
  };

  const handleAddMetricAsBigNumber = () => {
    if (!selectedMetric.metric) {
      alert("Please select a metric first");
      return;
    }
    console.log("Adding metric:", selectedMetric);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
    // Add the selected metric to the current template's metrics
    setCurrentTemplate(prevTemplate => ({
      ...prevTemplate,
      metrics: [...prevTemplate.metrics, {
        'type': selectedMetric.mainKey,
        ...JSON.parse(JSON.stringify(selectedMetric.metricInfo)),
        'user_given_name': selectedMetric.metricInfo.name,
        'layout_id': crypto.randomUUID()
      }]
    }));
    setSelectedMetric({
      mainKey: null,
      subKey: null,
      metric: null,
      metricInfo: null
    });
    console.log("Metric added:", selectedMetric);
  }

  const handleAddMetricAsTableMetrics = () => {
    if (!selectedMetric.items || selectedMetric.items.length === 0) {
      alert("Please select at least one metric to add to the table");
      return;
    }
    console.log("Adding table metrics:", selectedMetric.items);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
    // Add the entire table as a metric to the current template's metrics
    setCurrentTemplate(prevTemplate => ({
      ...prevTemplate,
      metrics: [...prevTemplate.metrics, {
        'type': selectedMetric.mainKey,
        'name': selectedMetric.mainKey === "Table (Firm Wide) KPIs" ? "Firm Wide KPIs Table" : "Projections KPIs Table",
        'user_given_name': selectedMetric.mainKey === "Table (Firm Wide) KPIs" ? "Firm Wide KPIs Table" : "Projections KPIs Table",
        'layout_id': crypto.randomUUID(),
        'data_type': 'table',
        'description': 'A table containing firm wide KPIs',
        'table_metrics': selectedMetric.items.map(item => ({
          ...JSON.parse(JSON.stringify(item.metricInfo)),
          'subKey': item.subKey
        }))
      }]
    }));
    setSelectedMetric({
      mainKey: null,
      items: []
    });
    console.log("Table metrics added:", selectedMetric.items);
  }

  const handleAddMetric = () => {
    if (selectedMetric.mainKey === "Big Number Card (KPIs)" || selectedMetric.mainKey === "Pie Chart" || selectedMetric.mainKey === "Miscellaneous") {
      handleAddMetricAsBigNumber();
    } else if (selectedMetric.mainKey === "Table (Firm Wide) KPIs" || selectedMetric.mainKey === "Table (Projections)"
      || selectedMetric.mainKey === "Product Analysis (KPIs)" || selectedMetric.mainKey === "Table (Pricing)" || selectedMetric.mainKey === "Chart (Projections)"
    ) {
      handleAddMetricAsTableMetrics();
    }
  }

  const populateTemplateOptions = useCallback(() => {
    if (Local_Template_Options["Product Analysis (KPIs)"].keys.length > 0) { return; } // Already populated

    dataToExport.productAnalyses.forEach(p => {
      Local_Template_Options["Product Analysis (KPIs)"].keys.push(p.name);
      console.log(productAnalysesKPIFields.items);

      let items_to_add = {};

      // Needed so we can allow editing product names to reuse templates
      Object.keys(productAnalysesKPIFields.items).forEach((value) => {
        items_to_add[value] = {
          ...productAnalysesKPIFields.items[value], "options": {
            "keys": ["Product Name"],
            "Product Name": {
              "value": p.name,
              "name": "Product Name"
            },
          }
        }
      })

      Local_Template_Options["Product Analysis (KPIs)"][p.name] = {
        description: `Metrics related to the product analysis for ${p.name}`,
        keys: productAnalysesKPIFields.keys, // Contains the metric names
        items: items_to_add // Contains the metric details
      };
    });


  }, [dataToExport]); // Needed to generate options dynamically

  useEffect(() => {
    populateTemplateOptions();
  }, [populateTemplateOptions]);

  return (
    <div className="template-maker-container">
      <h1>Template Maker for {productName}</h1>
      {!templateSelected ? <h3>Please select a template before proceeding.</h3> : <>
        <div className="form-row">
          <label htmlFor="template_name">Edit Template Name: </label>
          <input
            className="form-input"
            id="template_name"
            type="text"
            placeholder="Enter template name"
            value={currentTemplate.name}
            onChange={(e) => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
          />
        </div>

        <div className="metric-selection-section">
          <MetricSelector setSelectedMetric={triggerMetricShake} />
        </div>

        {selectedMetric.metric && selectedMetric.mainKey === "Big Number Card (KPIs)" && (
          <div className={`selected-metric-display ${isShaking ? "shake" : ""}`}>
            <h3>Selected Metric:</h3>
            <div>
              <strong>{selectedMetric.metric}</strong>
              <p>{selectedMetric.mainKey} → {selectedMetric.subKey}</p>
              <p>{selectedMetric.metricInfo?.description}</p>
            </div>
          </div>
        )}

        {((selectedMetric.items && selectedMetric.mainKey === "Table (Firm Wide) KPIs") ||
          (selectedMetric.items && selectedMetric.mainKey === "Table (Projections)") ||
          (selectedMetric.items && selectedMetric.mainKey === "Product Analysis (KPIs)") ||
          (selectedMetric.items && selectedMetric.mainKey === "Table (Pricing)") ||
          (selectedMetric.items && selectedMetric.mainKey === "Chart (Projections)")
        )
          && (
            <div className={`selected-metric-display ${isShaking ? "shake" : ""}`}>
              <h3>Metrics to Add to the Table:</h3>
              {selectedMetric.items && selectedMetric.items.map((item, index) => (
                <div className="item" key={index}>
                  <strong>{item.metric}</strong>
                  <p>{item.mainKey} → {item.subKey}</p>
                  <p>{item.metricInfo?.description}</p>
                </div>
              ))}
            </div>
          )
        }

        {(selectedMetric.metric && selectedMetric.mainKey === "Pie Chart") && (
          <div className={`selected-metric-display ${isShaking ? "shake" : ""}`}>
            <h3>Selected Metric for Pie Chart:</h3>
            <div>
              <strong>{selectedMetric.metric}</strong>
              <p>{selectedMetric.mainKey} → {selectedMetric.subKey}</p>
              <p>{selectedMetric.metricInfo?.description}</p>
            </div>
          </div>
        )}

        {selectedMetric.mainKey === "Miscellaneous" && (
          <div className={`selected-metric-display ${isShaking ? "shake" : ""}`}>
            <h3>Selected Metric for Miscellaneous:</h3>
            <div>
              <strong>{selectedMetric.metric}</strong>
              <p>{selectedMetric.mainKey} → {selectedMetric.subKey}</p>
              <p>{selectedMetric.metricInfo?.description}</p>
            </div>
          </div>
        )}
        <div className="button-section">
          {selectedMetric.mainKey &&
            <button type="button" onClick={handleAddMetric}>Add {Template_keys_description[selectedMetric.mainKey].type}</button>
          }
        </div>
        <div className="all_selected_metrics">
          <h3>All Metrics in the Template:</h3>
          {currentTemplate.metrics.length === 0 ? (
            <p>No metrics added yet.</p>
          ) : (
            <MetricsInfo setCurrentTemplate={setCurrentTemplate} currentTemplate={currentTemplate} />
          )}
        </div>
      </>
      }
    </div>
  );
}

function MetricsInfo({ setCurrentTemplate, currentTemplate }) {
  return (
    <div className="all-metrics-cont">
      {currentTemplate.metrics.map((metric, index) => (
        <MetricCard metric={metric} index={index} setCurrentTemplate={setCurrentTemplate} currentTemplate={currentTemplate} />
      ))}
      {/*<pre>{JSON.stringify(currentTemplate, null, 2)}</pre>*/}
    </div>
  );
}

function MetricCard({ metric, index, setCurrentTemplate, currentTemplate }) {
  const handleInputChange = (index, field, value, isOption = false) => {
    const updatedMetrics = [...currentTemplate.metrics];
    if (isOption) {
      updatedMetrics[index].options[field].value = value;
    } else {
      updatedMetrics[index][field] = value;
    }
    setCurrentTemplate(prevTemplate => ({
      ...prevTemplate,
      metrics: updatedMetrics
    }));
  }
  const handleDelete = () => {
    const updatedMetrics = currentTemplate.metrics.filter((metric, indx) => indx !== index);
    setCurrentTemplate(prevTemplate => ({
      ...prevTemplate,
      metrics: updatedMetrics
    }))
  }

  return (
    <div className="entity-card flex-column" key={index}>
      <Popconfirm
        title="Delete Metric"
        description="Are you sure to delete this entire metric?"
        onConfirm={handleDelete}
        onCancel={() => { }}
        okText="Yes"
        cancelText="No"
      >
        <DeleteOutlined className="metric-delete-icon" />
      </Popconfirm>
      <div className="form-row">
        <label htmlFor={metric.type + ' ' + index}>Metric Type:</label>
        <input name={metric.type + ' ' + index} style={{ opacity: 0.7 }} className="form-input" type="text" disabled value={metric.type} />
      </div>
      <div className="form-row">
        <label htmlFor={'user_give_name' + index}>Metric Name:</label>
        <input className="form-input" type="text" value={metric.user_given_name} onChange={(e) => handleInputChange(index, 'user_given_name', e.target.value)} />
      </div>
      <div className="form-row">
        <label htmlFor={'description' + index}>Metric Description:</label>
        <input className="form-input" type="text" value={metric.description} onChange={(e) => handleInputChange(index, 'description', e.target.value)} />
      </div>
      {metric.options && metric.options.keys && metric.options.keys.map((optKey, optIndex) => (
        <div className="form-row">
          <label htmlFor={optKey + ' ' + optIndex}>{metric.options[optKey].name}</label>
          <input
            key={optIndex}
            className="form-input"
            type="text"
            name={optKey + ' ' + optIndex}
            value={metric.options[optKey].value}
            onChange={(e) => { handleInputChange(index, optKey, e.target.value, true) }}
            placeholder={metric.options[optKey].name}
          />
        </div>
      ))}
      {metric.table_metrics && metric.table_metrics.map((tableMetric, tableMetricIndex) => (
        <TableMetricItems setCurrentTemplate={setCurrentTemplate} currentTemplate={currentTemplate} tableMetric={tableMetric} tableMetricIndex={tableMetricIndex} metricIndex={index} />
      ))
      }
    </div>
  )
}

function TableMetricItems({ tableMetric, tableMetricIndex, metricIndex, setCurrentTemplate, currentTemplate }) {
  // Allows us to edit options for table metric items
  const handleInputChange = (field, value) => {
    const updatedMetrics = [...currentTemplate.metrics];
    updatedMetrics[metricIndex].table_metrics[tableMetricIndex].options[field].value = value;
    setCurrentTemplate(prevTemplate => ({
      ...prevTemplate,
      metrics: updatedMetrics
    }));
  };

  const handleDelete = () => {
    const updatedMetrics = [...currentTemplate.metrics];
    updatedMetrics[metricIndex].table_metrics = updatedMetrics[metricIndex].table_metrics.filter((item, indx) => indx !== tableMetricIndex);
    setCurrentTemplate((prevTemplate) => ({
      ...prevTemplate,
      metrics: updatedMetrics
    }));
  }

  return (
    <div className="entity-card flex-column" key={metricIndex}>
      <Popconfirm
        title="Delete Table Item"
        description="Are you sure to delete this item in the table?"
        onConfirm={handleDelete}
        onCancel={() => { }}
        okText="Yes"
        cancelText="No"
      >
        <DeleteOutlined className="metric-delete-icon" />
      </Popconfirm>
      <div className="form-row">
        <label htmlFor={tableMetric.name + ' ' + tableMetricIndex}>Name:</label>
        <input name={tableMetric.name + ' ' + tableMetricIndex} style={{ opacity: 0.7 }} className="form-input" type="text" disabled value={tableMetric.name} />
      </div>
      <div className="form-row">
        <label htmlFor={tableMetric.data_type + ' ' + tableMetricIndex}>Data Type:</label>
        <input name={tableMetric.data_type + ' ' + tableMetricIndex} style={{ opacity: 0.7 }} className="form-input" type="text" disabled value={tableMetric.data_type} />
      </div>
      {tableMetric.options && tableMetric.options.keys && tableMetric.options.keys.map((optKey, optIndex) => (
        <div className="form-row">
          <label htmlFor={optKey + ' ' + optIndex}>{tableMetric.options[optKey].name}</label>
          <input
            key={optIndex}
            className="form-input"
            type="text"
            name={optKey + ' ' + optIndex}
            value={tableMetric.options[optKey].value}
            placeholder={tableMetric.options[optKey].name}
            onChange={(e) => { handleInputChange(optKey, e.target.value, true) }}
          />
        </div>
      ))}
    </div>
  )
}

export default TemplateMaker;